/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { weightBold, weightMedium } from 'src/components/css'

interface RosterMemberCellProps {
  name: string
  initials: string
  imageUrl?: string
}

/** Round avatar + name, matching the artifact's roster row treatment. */
const RosterMemberCell = ({
  name,
  initials,
  imageUrl,
}: RosterMemberCellProps) => {
  'use memo'

  // A dead image URL would otherwise render the browser's broken-image glyph;
  // falling back to initials keeps every row looking deliberate.
  const [failed, setFailed] = useState(false)
  const showImage = Boolean(imageUrl) && !failed

  return (
    <span css={rowStyle}>
      {showImage ? (
        <img
          css={avatarStyle}
          src={imageUrl}
          alt=''
          onError={() => setFailed(true)}
        />
      ) : (
        <span css={avatarStyle} aria-hidden>
          {initials}
        </span>
      )}
      <span css={nameStyle}>{name}</span>
    </span>
  )
}

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
`

const avatarStyle = css`
  ${weightBold}
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: 50%;
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  display: flex;
  align-items: center;
  justify-content: center;
  /* contain, not cover — an uploaded avatar must not be cropped oddly */
  object-fit: contain;
  font-size: 10px;
`

const nameStyle = css`
  ${weightMedium}
  color: ${colors.NEUTRAL_0};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default RosterMemberCell
